import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Drawer,
  Stack,
  Text,
  Button,
  Group,
  Divider,
  ScrollArea,
  Center,
  ActionIcon
} from '@mantine/core';
import {
  IconShoppingBag,
  IconTrash,
  IconArrowRight
} from '@tabler/icons-react';
import { selectCartItems, selectCartTotal, selectCartItemCount, clearCart } from '../../store/slices/cartSlice';
import CartItem from './CartItem';

const CartDrawer = ({ opened, onClose }) => {
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const itemCount = useSelector(selectCartItemCount);

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleCheckout = () => {
    // TODO: Implement checkout functionality
    console.log('Proceeding to checkout with items:', cartItems);
    onClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconShoppingBag size="1.2rem" />
          <Text fw={600}>Shopping Cart</Text>
          {itemCount > 0 && (
            <Text size="sm" c="dimmed">({itemCount} items)</Text>
          )}
        </Group>
      }
      position="right"
      size="md"
      padding="md"
    >
      <Stack gap="md" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
        {cartItems.length === 0 ? (
          // Empty cart state
          <Center style={{ flex: 1 }}>
            <Stack gap="md" align="center">
              <IconShoppingBag size="3rem" color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" ta="center">Your cart is empty</Text>
              <Button 
                component={Link} 
                to="/products" 
                onClick={onClose}
                leftSection={<IconShoppingBag size="1rem" />}
              >
                Start Shopping
              </Button>
            </Stack>
          </Center>
        ) : (
          <>
            {/* Cart Header */}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} in cart
              </Text>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={handleClearCart}
                title="Clear cart"
              >
                <IconTrash size="1rem" />
              </ActionIcon>
            </Group>

            <Divider />

            {/* Cart Items */}
            <ScrollArea style={{ flex: 1 }} offsetScrollbars>
              <Stack gap="md">
                {cartItems.map((item) => (
                  <div key={item.id}>
                    <CartItem item={item} compact={true} />
                    <Divider mt="md" />
                  </div>
                ))}
              </Stack>
            </ScrollArea>

            {/* Cart Summary */}
            <Stack gap="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)', paddingTop: 16 }}>
              <Group justify="space-between">
                <Text size="lg" fw={600}>Total:</Text>
                <Text size="xl" fw={700} c="blue">${cartTotal}</Text>
              </Group>

              <Stack gap="sm">
                <Button
                  size="lg"
                  fullWidth
                  onClick={handleCheckout}
                  rightSection={<IconArrowRight size="1rem" />}
                  disabled={cartItems.some(item => !item.is_available || item.quantity > item.stock_quantity)}
                >
                  Proceed to Checkout
                </Button>
                
                <Button
                  variant="outline"
                  size="md"
                  fullWidth
                  component={Link}
                  to="/cart"
                  onClick={onClose}
                >
                  View Full Cart
                </Button>
              </Stack>

              {cartItems.some(item => !item.is_available || item.quantity > item.stock_quantity) && (
                <Text size="xs" c="red" ta="center">
                  Some items are no longer available or out of stock
                </Text>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Drawer>
  );
};

export default CartDrawer;